import {
  Box,
  CommonViewPopup,
  customUseEffect,
  DetailsPageFooter,
  Divider,
  getCreatedBy,
  getUpdatedBy,
} from "@common";
import {
  getCampaignDetailsById,
  getCampaignTargetMetricByCampaignId,
  getInquiryDetailsById,
  getInquiryQualificationByInquiryId,
  getInquirySourceByInquiryId,
  getInquiryTaggingById,
  getLeadDetailsById,
  getLeadQualificationByLeadId,
  getLeadSourceByLeadId,
  getLeadTaggingById,
  getProspectContactCommunicationByProspectContactId,
  getProspectContactDetailsById,
  getProspectContactEngagementByProspectContactId,
  getProspectDetailsById,
  getProspectInterestByProspectId,
  getProspectQualificationByProspectId,
  getProspectSourceByProspectId,
} from "@features";
import { dateTimeFormat, KanbanDetailsContent } from "@pionglobal/pionsharedui";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getBasicInformationView as getCampaignBasicInformationView,
  getCampaignTargetMetricView,
} from "../../generate/campaign/campaign-details/utils/campaignDetailsView";
import {
  getBasicInformationView as getInquiryBasicInformationView,
  getInquiryQualificationView,
  getInquirySourceView,
  getInquiryTaggingView,
} from "../../generate/inquiry/inquiry-details/utils/inquiryDetailsView";
import {
  EMPTY_VALUE,
  getValue,
  getBasicInformationView as getLeadBasicInformationView,
  getLeadQualificationView,
  getLeadSourceView,
  getLeadTaggingView,
} from "../../generate/lead/lead-details/utils/leadDetailsView";
import { getProspectContactBasicInfoView } from "../../generate/prospect/prospect-details/contact/view-details/utils/prospectContactBasicInfoView";
import { getProspectContactCommunicationView } from "../../generate/prospect/prospect-details/contact/view-details/utils/prospectContactCommunicationView";
import { getProspectContactEngagementView } from "../../generate/prospect/prospect-details/contact/view-details/utils/prospectContactEngagementView";
import {
  getBasicInformationView as getProspectBasicInformationView,
  getProspectInterestView,
  getProspectQualificationView,
  getProspectSourceView,
} from "../../generate/prospect/prospect-details/utils/prospectDetailsView";

const getId = (entity, idField) => {
  if (!entity && !idField) return null;
  if (typeof entity === "object" && entity !== null) {
    return entity.id || entity[idField] || null;
  }
  return entity || idField || null;
};

const HEADING_TITLES = {
  lead: "Lead Details",
  inquiry: "Inquiry Details",
  campaign: "Campaign Details",
  prospect: "Prospect Details",
  prospectContact: "Prospect Contact Details",
};

// -----------------------------------------------------------------------------
// Pure Segment Builders (low cognitive complexity)
// -----------------------------------------------------------------------------
const getLeadSegments = (
  leadData,
  leadSourceRecord,
  leadQualificationRecord,
  leadTaggingRecord
) => {
  const basicView = getLeadBasicInformationView(leadData);
  const sourceView = getLeadSourceView(leadSourceRecord);
  const qualificationView = getLeadQualificationView(leadQualificationRecord);
  const taggingView = getLeadTaggingView(leadTaggingRecord);

  return [
    {
      id: "lead_basic",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 8 },
      title: "Basic Information",
      left: [
        {
          label: "Lead No",
          value: getValue(leadData?.leadNo),
        },
        {
          label: "Lead Name",
          value: getValue(leadData?.leadName || leadData?.name),
        },
        ...(basicView?.left || []),
      ],
      right: [
        {
          label: "Status",
          value: getValue(leadData?.status),
        },
        ...(basicView?.right || []),
      ],
      fullWidth: basicView?.fullWidth || [],
      editable: false,
    },
    {
      id: "lead_source",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 4 },
      title: "Lead Source",
      left: sourceView?.left || [],
      right: sourceView?.right || [],
      fullWidth: sourceView?.fullWidth || [],
      editable: false,
    },
    {
      id: "lead_qualification",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 8 },
      title: "Lead Qualification",
      left: qualificationView?.left || [],
      right: qualificationView?.right || [],
      fullWidth: qualificationView?.fullWidth || [],
      editable: false,
    },
    {
      id: "lead_tagging",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 4 },
      title: "Lead Tagging",
      left: taggingView?.left || [],
      right: taggingView?.right || [],
      fullWidth: taggingView?.fullWidth || [],
      editable: false,
    },
  ];
};

const getCampaignSegments = (campaignData, campaignTargetMetricRecord) => {
  const basicView = getCampaignBasicInformationView(campaignData);
  const metricView = getCampaignTargetMetricView(
    campaignTargetMetricRecord,
    campaignData
  );

  return [
    {
      id: "campaign_basic",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 7 },
      title: "Basic Information",
      left: [
        {
          label: "Campaign Name",
          value: getValue(campaignData?.campaignName || campaignData?.name),
        },
        {
          label: "Campaign Type",
          value: getValue(campaignData?.campaignType),
        },
        {
          label: "Campaign Category",
          value: getValue(campaignData?.campaignCategory),
        },
        ...(basicView?.left || []),
      ],
      right: [
        {
          label: "Campaign No",
          value: getValue(campaignData?.campaignNo),
        },
        {
          label: "Status",
          value: getValue(campaignData?.status),
        },
        ...(basicView?.right || []),
      ],
      fullWidth: basicView?.fullWidth || [],
      editable: false,
    },
    {
      id: "campaign_target_metric",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 5 },
      title: "Target Metric",
      left: metricView?.left || [],
      right: metricView?.right || [],
      fullWidth: metricView?.fullWidth || [],
      editable: false,
    },
  ];
};

const getProspectSegments = (
  prospectData,
  prospectInterestRecord,
  prospectQualificationRecord,
  prospectSourceRecord
) => {
  const basicView = getProspectBasicInformationView(prospectData);
  const interestView = getProspectInterestView(prospectInterestRecord);
  const qualificationView = getProspectQualificationView(
    prospectQualificationRecord
  );
  const sourceView = getProspectSourceView(prospectSourceRecord);

  return [
    {
      id: "prospect_basic",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 8 },
      title: "Basic Information",
      left: [
        {
          label: "Prospect Name",
          value: getValue(prospectData?.prospectName || prospectData?.name),
        },
        {
          label: "Prospect Type",
          value: getValue(prospectData?.prospectType),
        },
        ...(basicView?.left || []),
      ],
      right: [
        {
          label: "Prospect No",
          value: getValue(prospectData?.prospectNo),
        },
        {
          label: "Status",
          value: getValue(prospectData?.status),
        },
        ...(basicView?.right || []),
      ],
      fullWidth: basicView?.fullWidth || [],
      editable: false,
    },
    {
      id: "prospect_interest",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 4 },
      title: "Product Interest",
      left: interestView?.left || [],
      right: interestView?.right || [],
      fullWidth: interestView?.fullWidth || [],
      editable: false,
    },
    {
      id: "prospect_qualification",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 8 },
      title: "Qualification",
      left: qualificationView?.left || [],
      right: qualificationView?.right || [],
      fullWidth: qualificationView?.fullWidth || [],
      editable: false,
    },
    {
      id: "prospect_source",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 4 },
      title: "Source",
      left: sourceView?.left || [],
      right: sourceView?.right || [],
      fullWidth: sourceView?.fullWidth || [],
      editable: false,
    },
  ];
};

const getInquirySegments = (
  inquiryData,
  inquirySourceRecord,
  inquiryQualificationRecord,
  inquiryTaggingRecord
) => {
  const basicView = getInquiryBasicInformationView(inquiryData);
  const sourceView = getInquirySourceView(inquirySourceRecord);
  const qualificationView = getInquiryQualificationView(
    inquiryQualificationRecord
  );
  const taggingView = getInquiryTaggingView(inquiryTaggingRecord);

  return [
    {
      id: "inquiry_basic",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 8 },
      title: "Basic Information",
      left: [
        {
          label: "Inquiry No",
          value: getValue(inquiryData?.inquiryNo),
        },
        {
          label: "Inquiry Name",
          value: getValue(inquiryData?.inquiryName),
        },
        ...(basicView?.left || []),
      ],
      right: [
        {
          label: "Status",
          value: getValue(inquiryData?.status),
        },
        ...(basicView?.right || []),
      ],
      fullWidth: basicView?.fullWidth || [],
      editable: false,
    },
    {
      id: "inquiry_source",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 4 },
      title: "Inquiry Source",
      left: sourceView?.left || [],
      right: sourceView?.right || [],
      fullWidth: sourceView?.fullWidth || [],
      editable: false,
    },
    {
      id: "inquiry_qualification",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 8 },
      title: "Inquiry Qualification",
      left: qualificationView?.left || [],
      right: qualificationView?.right || [],
      fullWidth: qualificationView?.fullWidth || [],
      editable: false,
    },
    {
      id: "inquiry_tagging",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 4 },
      title: "Inquiry Tagging",
      left: taggingView?.left || [],
      right: taggingView?.right || [],
      fullWidth: taggingView?.fullWidth || [],
      editable: false,
    },
  ];
};

const getProspectContactSegments = (
  prospectContactData,
  prospectContactCommunicationRecord,
  prospectContactEngagementRecord
) => {
  const contactBasicView = getProspectContactBasicInfoView(prospectContactData);
  const contactCommView = getProspectContactCommunicationView(
    prospectContactCommunicationRecord
  );
  const contactEngView = getProspectContactEngagementView(
    prospectContactEngagementRecord
  );

  return [
    {
      id: "contact_basic_info",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 6 },
      title: "Contact Information",
      left: contactBasicView?.left || [],
      right: contactBasicView?.right || [],
      fullWidth: contactBasicView?.fullWidth || [],
      editable: false,
    },
    {
      id: "contact_communication",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 6 },
      title: "Communication",
      left: contactCommView?.left || [],
      right: contactCommView?.right || [],
      fullWidth: contactCommView?.fullWidth || [],
      editable: false,
    },
    {
      id: "contact_engagement",
      visible: true,
      gridSize: { xs: 12, sm: 12, md: 12, lg: 12 },
      title: "Engagement",
      left: contactEngView?.left || [],
      right: contactEngView?.right || [],
      fullWidth: contactEngView?.fullWidth || [],
      editable: false,
    },
  ];
};

const CommonTaggingCardPopup = ({
  open = false,
  onClose,
  type = "inquiry", // "lead" | "inquiry" | "campaign" | "prospect" | "prospectContact"
  data = {},
}) => {
  const dispatch = useDispatch();

  // Redux state stores
  const leadStore = useSelector((state) => state?.lead || {});
  const leadSourceStore = useSelector((state) => state?.leadSource || {});
  const leadQualificationStore = useSelector(
    (state) => state?.leadQualification || {}
  );
  const leadTaggingStore = useSelector((state) => state?.leadTagging || {});

  const inquiryStore = useSelector((state) => state?.inquiry || {});
  const inquirySourceStore = useSelector((state) => state?.inquirySource || {});
  const inquiryQualificationStore = useSelector(
    (state) => state?.inquiryQualification || {}
  );
  const inquiryTaggingStore = useSelector(
    (state) => state?.inquiryTagging || {}
  );

  const campaignStore = useSelector((state) => state?.campaign || {});
  const campaignTargetMetricStore = useSelector(
    (state) => state?.campaignTargetMetric || {}
  );

  const prospectStore = useSelector((state) => state?.prospect || {});
  const prospectInterestStore = useSelector(
    (state) => state?.prospectInterest || {}
  );
  const prospectQualificationStore = useSelector(
    (state) => state?.prospectQualification || {}
  );
  const prospectSourceStore = useSelector(
    (state) => state?.prospectSource || {}
  );

  const prospectContactStore = useSelector(
    (state) => state?.prospectContact || {}
  );
  const prospectContactCommunicationStore = useSelector(
    (state) => state?.prospectContactCommunication || {}
  );
  const prospectContactEngagementStore = useSelector(
    (state) => state?.prospectContactEngagement || {}
  );

  // Resolved Entity IDs
  const leadId = useMemo(
    () => getId(data?.lead, data?.leadId),
    [data?.lead, data?.leadId]
  );
  const inquiryId = useMemo(
    () => getId(data?.inquiry, data?.inquiryId),
    [data?.inquiry, data?.inquiryId]
  );
  const campaignId = useMemo(
    () => getId(data?.campaign, data?.campaignId),
    [data?.campaign, data?.campaignId]
  );
  const prospectId = useMemo(
    () => getId(data?.prospect, data?.prospectId),
    [data?.prospect, data?.prospectId]
  );
  const prospectContactId = useMemo(
    () => getId(data?.prospectContact, data?.prospectContactId),
    [data?.prospectContact, data?.prospectContactId]
  );

  // Fetch all segments when popup opens
  customUseEffect(() => {
    if (!open) return;

    if (type === "lead" && leadId) {
      dispatch(getLeadDetailsById({ id: leadId }));
      dispatch(getLeadSourceByLeadId({ leadId }));
      dispatch(getLeadQualificationByLeadId({ leadId }));
      dispatch(getLeadTaggingById({ leadId }));
    } else if (type === "campaign" && campaignId) {
      dispatch(getCampaignDetailsById({ id: campaignId }));
      dispatch(getCampaignTargetMetricByCampaignId({ campaignId }));
    } else if (type === "prospect" && prospectId) {
      dispatch(getProspectDetailsById({ id: prospectId }));
      dispatch(getProspectInterestByProspectId({ prospectId }));
      dispatch(getProspectQualificationByProspectId({ prospectId }));
      dispatch(getProspectSourceByProspectId({ prospectId }));
    } else if (type === "inquiry" && inquiryId) {
      dispatch(getInquiryDetailsById({ id: inquiryId }));
      dispatch(getInquirySourceByInquiryId({ inquiryId }));
      dispatch(getInquiryQualificationByInquiryId({ inquiryId }));
      dispatch(getInquiryTaggingById({ inquiryId }));
    } else if (type === "prospectContact" && prospectContactId) {
      dispatch(getProspectContactDetailsById({ id: prospectContactId }));
      dispatch(
        getProspectContactCommunicationByProspectContactId({
          prospectContactId,
        })
      );
      dispatch(
        getProspectContactEngagementByProspectContactId({
          prospectContactId,
        })
      );
    }
  }, [open, type, leadId, inquiryId, campaignId, prospectId, prospectContactId]);

  // Primary data objects
  const leadData = useMemo(() => {
    const fromDetails = Array.isArray(leadStore.details)
      ? leadStore.details[0]
      : leadStore.details;
    const fromOptions = Array.isArray(leadStore.data)
      ? leadStore.data.find((item) => item?.id === leadId)
      : null;
    const fromData = typeof data?.lead === "object" ? data.lead : null;
    return { ...fromOptions, ...fromData, ...fromDetails };
  }, [leadStore.details, leadStore.data, data?.lead, leadId]);

  const leadSourceRecord = useMemo(() => {
    const details = leadSourceStore.details;
    if (!details) return {};
    return Array.isArray(details) ? details[0] ?? {} : details;
  }, [leadSourceStore.details]);

  const leadQualificationRecord = useMemo(() => {
    const details = leadQualificationStore.details;
    if (!details) return {};
    return Array.isArray(details) ? details[0] ?? {} : details;
  }, [leadQualificationStore.details]);

  const leadTaggingRecord = useMemo(() => {
    const details = leadTaggingStore.details || leadTaggingStore.data;
    if (!details) return {};
    return Array.isArray(details) ? details[0] ?? {} : details;
  }, [leadTaggingStore.details, leadTaggingStore.data]);

  const inquiryData = useMemo(() => {
    const fromDetails = Array.isArray(inquiryStore.details)
      ? inquiryStore.details[0]
      : inquiryStore.details;
    const fromOptions = Array.isArray(inquiryStore.data)
      ? inquiryStore.data.find((item) => item?.id === inquiryId)
      : null;
    const fromData = typeof data?.inquiry === "object" ? data.inquiry : null;
    return { ...fromOptions, ...fromData, ...fromDetails };
  }, [inquiryStore.details, inquiryStore.data, data?.inquiry, inquiryId]);

  const campaignData = useMemo(() => {
    const fromDetails = Array.isArray(campaignStore.details)
      ? campaignStore.details[0]
      : campaignStore.details;
    const fromOptions = Array.isArray(campaignStore.data)
      ? campaignStore.data.find((item) => item?.id === campaignId)
      : null;
    const fromData = typeof data?.campaign === "object" ? data.campaign : null;
    return { ...fromOptions, ...fromData, ...fromDetails };
  }, [campaignStore.details, campaignStore.data, data?.campaign, campaignId]);

  const prospectData = useMemo(() => {
    const fromDetails = Array.isArray(prospectStore.details)
      ? prospectStore.details[0]
      : prospectStore.details;
    const fromOptions = Array.isArray(prospectStore.data)
      ? prospectStore.data.find((item) => item?.id === prospectId)
      : null;
    const fromData = typeof data?.prospect === "object" ? data.prospect : null;
    return { ...fromOptions, ...fromData, ...fromDetails };
  }, [prospectStore.details, prospectStore.data, data?.prospect, prospectId]);

  const prospectContactData = useMemo(() => {
    const fromDetails = Array.isArray(prospectContactStore.details)
      ? prospectContactStore.details[0]
      : prospectContactStore.details;
    const fromOptions = Array.isArray(prospectContactStore.data)
      ? prospectContactStore.data.find((item) => item?.id === prospectContactId)
      : null;
    const fromData =
      typeof data?.prospectContact === "object" ? data.prospectContact : null;
    return { ...fromOptions, ...fromData, ...fromDetails };
  }, [
    prospectContactStore.details,
    prospectContactStore.data,
    data?.prospectContact,
    prospectContactId,
  ]);

  // Sub-segment records
  const campaignTargetMetricRecord = useMemo(() => {
    const details = campaignTargetMetricStore.details;
    if (!details) return {};
    return Array.isArray(details) ? details[0] ?? {} : details;
  }, [campaignTargetMetricStore.details]);

  const prospectInterestRecord = useMemo(() => {
    const details = prospectInterestStore.details;
    if (!details) return {};
    return Array.isArray(details) ? details[0] ?? {} : details;
  }, [prospectInterestStore.details]);

  const prospectQualificationRecord = useMemo(() => {
    const details = prospectQualificationStore.details;
    if (!details) return {};
    return Array.isArray(details) ? details[0] ?? {} : details;
  }, [prospectQualificationStore.details]);

  const prospectSourceRecord = useMemo(() => {
    const details = prospectSourceStore.details;
    if (!details) return {};
    return Array.isArray(details) ? details[0] ?? {} : details;
  }, [prospectSourceStore.details]);

  const inquirySourceRecord = useMemo(() => {
    const details = inquirySourceStore.details;
    if (!details) return {};
    return Array.isArray(details) ? details[0] ?? {} : details;
  }, [inquirySourceStore.details]);

  const inquiryQualificationRecord = useMemo(() => {
    const details = inquiryQualificationStore.details;
    if (!details) return {};
    return Array.isArray(details) ? details[0] ?? {} : details;
  }, [inquiryQualificationStore.details]);

  const inquiryTaggingRecord = useMemo(() => {
    const details = inquiryTaggingStore.details || inquiryTaggingStore.data;
    if (!details) return {};
    return Array.isArray(details) ? details[0] ?? {} : details;
  }, [inquiryTaggingStore.details, inquiryTaggingStore.data]);

  const prospectContactCommunicationRecord = useMemo(() => {
    const details = prospectContactCommunicationStore.details;
    const commData = Array.isArray(details) ? details[0] ?? {} : details || {};
    return {
      phone: prospectContactData?.phone,
      dialingCodePhone: prospectContactData?.dialingCodePhone,
      mobile: prospectContactData?.mobile,
      dialingCodeMobile: prospectContactData?.dialingCodeMobile,
      email: prospectContactData?.email,
      alternateEmail: prospectContactData?.alternateEmail,
      ...commData,
    };
  }, [prospectContactCommunicationStore.details, prospectContactData]);

  const prospectContactEngagementRecord = useMemo(() => {
    const details = prospectContactEngagementStore.details;
    if (!details) return {};
    return Array.isArray(details) ? details[0] ?? {} : details;
  }, [prospectContactEngagementStore.details]);

  const headingTitle = useMemo(() => HEADING_TITLES[type] || "Details", [type]);

  const activeRecord = useMemo(() => {
    switch (type) {
      case "lead":
        return leadData;
      case "inquiry":
        return inquiryData;
      case "campaign":
        return campaignData;
      case "prospect":
        return prospectData;
      case "prospectContact":
        return prospectContactData;
      default:
        return {};
    }
  }, [type, leadData, inquiryData, campaignData, prospectData, prospectContactData]);

  // All segments for each entity type
  const segments = useMemo(() => {
    switch (type) {
      case "lead":
        return getLeadSegments(
          leadData,
          leadSourceRecord,
          leadQualificationRecord,
          leadTaggingRecord
        );
      case "campaign":
        return getCampaignSegments(campaignData, campaignTargetMetricRecord);
      case "prospect":
        return getProspectSegments(
          prospectData,
          prospectInterestRecord,
          prospectQualificationRecord,
          prospectSourceRecord
        );
      case "inquiry":
        return getInquirySegments(
          inquiryData,
          inquirySourceRecord,
          inquiryQualificationRecord,
          inquiryTaggingRecord
        );
      case "prospectContact":
        return getProspectContactSegments(
          prospectContactData,
          prospectContactCommunicationRecord,
          prospectContactEngagementRecord
        );
      default:
        return [];
    }
  }, [
    type,
    leadData,
    leadSourceRecord,
    leadQualificationRecord,
    leadTaggingRecord,
    campaignData,
    campaignTargetMetricRecord,
    prospectData,
    prospectInterestRecord,
    prospectQualificationRecord,
    prospectSourceRecord,
    inquiryData,
    inquirySourceRecord,
    inquiryQualificationRecord,
    inquiryTaggingRecord,
    prospectContactData,
    prospectContactCommunicationRecord,
    prospectContactEngagementRecord,
  ]);

  if (!open) return null;

  const auditCreatedBy =
    getCreatedBy(activeRecord?.user_created) ||
    activeRecord?.createdBy ||
    EMPTY_VALUE;

  const auditUpdatedBy =
    getUpdatedBy(activeRecord?.user_updated) ||
    activeRecord?.updatedBy ||
    EMPTY_VALUE;

  const auditCreatedOn = activeRecord?.createdOn
    ? dateTimeFormat(activeRecord.createdOn, true)
    : EMPTY_VALUE;

  const auditUpdatedOn = activeRecord?.updatedOn
    ? dateTimeFormat(activeRecord.updatedOn, true)
    : EMPTY_VALUE;

  const hasAudit = Boolean(
    auditCreatedBy || auditUpdatedBy || auditCreatedOn || auditUpdatedOn
  );

  return (
    <CommonViewPopup
      heading={headingTitle}
      close={onClose}
      buttonAlignRight={true}
      cancelButtonName="Close"
      largeScreen={true}
    >
      <Box sx={{ width: "100%", p: 0 }}>
        <KanbanDetailsContent segments={segments} />

        {hasAudit && (
          <Box sx={{ px: 1, pt: 1.5, pb: 0.5 }}>
            <Divider sx={{ mb: 1 }} />
            <DetailsPageFooter
              sx={{ mb: 0 }}
              createdBy={auditCreatedBy}
              updatedBy={auditUpdatedBy}
              createdOn={auditCreatedOn}
              updatedOn={auditUpdatedOn}
            />
          </Box>
        )}
      </Box>
    </CommonViewPopup>
  );
};

export default React.memo(CommonTaggingCardPopup);

